import { env } from "./env";

export type PresignResponse = {
  url: string;
  r2Key: string;
  uploadHeaders: Record<string, string>;
  alreadyExists: boolean;
};

export type FinalizeResponse = { id: string; deduped: boolean };

async function sha256Hex(file: Blob): Promise<string> {
  const buf = await file.arrayBuffer();
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return [...new Uint8Array(hash)]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Drive the full upload pipeline for one file and return the media row id.
 *
 *   sha256 → /api/uploads/presign → R2 PUT → /api/uploads/finalize → mediaId
 *
 * Throws on any non-ok response; caller can show a toast.
 */
export async function uploadOne(
  file: File,
  onProgress?: (
    phase: "hash" | "presign" | "put" | "finalize" | "done",
  ) => void,
): Promise<{ mediaId: string; r2Key: string; deduped: boolean }> {
  onProgress?.("hash");
  const hash = await sha256Hex(file);

  onProgress?.("presign");
  const presignRes = await fetch(`${env.apiUrl}/api/uploads/presign`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      contentHash: hash,
      mimeType: file.type || "application/octet-stream",
      byteSize: file.size,
      originalFilename: file.name,
    }),
  });
  if (!presignRes.ok)
    throw new Error(`presign ${presignRes.status}: ${await presignRes.text()}`);
  const presign = (await presignRes.json()) as PresignResponse;

  if (!presign.alreadyExists) {
    onProgress?.("put");
    const putRes = await fetch(presign.url, {
      method: "PUT",
      headers: presign.uploadHeaders,
      body: file,
    });
    if (!putRes.ok)
      throw new Error(`r2 PUT ${putRes.status}: ${await putRes.text()}`);
  }

  onProgress?.("finalize");
  const finRes = await fetch(`${env.apiUrl}/api/uploads/finalize`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      r2Key: presign.r2Key,
      contentHash: hash,
      mimeType: file.type || "application/octet-stream",
      byteSize: file.size,
      originalFilename: file.name,
    }),
  });
  if (!finRes.ok)
    throw new Error(`finalize ${finRes.status}: ${await finRes.text()}`);
  const finalize = (await finRes.json()) as FinalizeResponse;

  onProgress?.("done");
  return {
    mediaId: finalize.id,
    r2Key: presign.r2Key,
    deduped: finalize.deduped,
  };
}
